import { computed, inject, ref, type Ref } from 'vue'
import { useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'

import { ConsultationRequestStatusDTO, type ConsultationRequestDTO, UrgencyDTO } from '@/generated'
import { useConsultationRequest } from '@/composables/useConsultationRequest'
import { useConsultationRequestStore } from '@/stores/consultationRequest'
import { useCallStore } from '@/stores/call'
import { useUserStore } from '@/stores/user'
import { routeNames } from '@/router/routeNames'
import { PostDeleteConsultationRequestActionKey } from '@/util/symbols'
import { ActionType } from '@/enums/ActionType'

type DirectConsultationActionType = ActionType

export interface Action {
  type: ActionType
  actionName: string
  actionCallback: () => Promise<void> | void
  showAction: boolean
  disable?: boolean
  isLoading?: boolean
}

function useConsultationActions(consultation: Ref<ConsultationRequestDTO>) {
  const postDeleteConsultationRequestAction = inject(
    PostDeleteConsultationRequestActionKey,
    () => {}
  )
  const { t } = useI18n()
  const router = useRouter()

  const { hasGodModeShift } = storeToRefs(useUserStore())
  const callStore = useCallStore()

  const {
    declineConsultationRequest,
    acceptConsultationRequest,
    isIncomingRequest,
    isOutgoingRequest,
    userCanAccept,
    userCanDecline,
    userCanStartCall,
    userCanWriteReport,
    userCanDelete
  } = useConsultationRequest(consultation)

  const {
    deleteOutgoingDirectConsultationById,
    refreshIncomingConsultationRequestList,
    refreshOutgoingConsultationRequestList
  } = useConsultationRequestStore()

  const startCallInProgress = ref(false)
  const acceptInProgress = ref(false)

  async function refreshAfterMutation(): Promise<void> {
    if (isIncomingRequest.value) {
      await refreshIncomingConsultationRequestList()
    } else if (isOutgoingRequest.value) {
      await refreshOutgoingConsultationRequestList()
    }
  }

  async function startCall(): Promise<void> {
    if (startCallInProgress.value) return

    startCallInProgress.value = true
    try {
      await callStore.startCall(consultation.value.id)
      await refreshAfterMutation()
    } finally {
      startCallInProgress.value = false
    }
  }

  async function acceptCall(): Promise<void> {
    if (acceptInProgress.value) return

    acceptInProgress.value = true
    try {
      await acceptConsultationRequest()
      await refreshAfterMutation()

      if (consultation.value.urgency !== UrgencyDTO.EMERGENCY) {
        await router.push({ name: routeNames.CONSULTATIONS })
      }
    } finally {
      acceptInProgress.value = false
    }
  }

  async function onDelete(): Promise<void> {
    await deleteOutgoingDirectConsultationById(consultation.value.id)
    postDeleteConsultationRequestAction()
  }

  const consultationActions = computed<Record<DirectConsultationActionType, Action>>(() => {
    return {
      [ActionType.ACCEPT]: {
        type: ActionType.ACCEPT,
        actionName: t('Accept'),
        actionCallback: acceptCall,
        showAction: userCanAccept.value,
        disable: hasGodModeShift.value,
        isLoading: acceptInProgress.value
      },
      [ActionType.START_CALL]: {
        type: ActionType.START_CALL,
        actionName: t('Join video call'),
        actionCallback: startCall,
        showAction: userCanStartCall.value,
        disable: hasGodModeShift.value,
        isLoading: startCallInProgress.value
      },
      [ActionType.DECLINE]: {
        type: ActionType.DECLINE,
        actionName: t('Decline'),
        actionCallback: declineConsultationRequest,
        showAction: isIncomingRequest.value && userCanDecline.value,
        disable: hasGodModeShift.value
      },
      [ActionType.WRITE_REPORT]: {
        type: ActionType.WRITE_REPORT,
        actionName: t('Write report'),
        actionCallback: () => {
          router.push({
            name: routeNames.WRITE_DIRECT_CONSULTATION_REPORT,
            params: { consultationRequestId: consultation.value.id }
          })
        },
        showAction: userCanWriteReport.value,
        disable: hasGodModeShift.value
      },
      [ActionType.DELETE]: {
        type: ActionType.DELETE,
        actionName: t('Delete consultation'),
        actionCallback: onDelete,
        showAction: userCanDelete.value,
        disable: hasGodModeShift.value
      }
    }
  })

  const consultationActionsPerStatus = computed<Action[]>(() => {
    const startCallAction = consultationActions.value[ActionType.START_CALL]
    const declineAction = consultationActions.value[ActionType.DECLINE]
    const writeReportAction = consultationActions.value[ActionType.WRITE_REPORT]
    const deleteAction = consultationActions.value[ActionType.DELETE]
    const acceptAction = consultationActions.value[ActionType.ACCEPT]

    const result: Array<Action | undefined> = []

    if (consultation.value.urgency === UrgencyDTO.EMERGENCY) {
      const routingFailed = isOutgoingRequest.value && !consultation.value.recipientsAvailable

      switch (consultation.value.status) {
        case ConsultationRequestStatusDTO.CREATED:
          if (routingFailed) {
            result.push(deleteAction)
          } else {
            result.push(startCallAction)
          }
          break
        case ConsultationRequestStatusDTO.ACCEPTED:
        case ConsultationRequestStatusDTO.IN_PROGRESS:
        case ConsultationRequestStatusDTO.REPORT_PENDING:
          if (routingFailed) {
            result.push(deleteAction)
          } else {
            result.push(startCallAction, writeReportAction)
          }
          break
        case ConsultationRequestStatusDTO.DELETED:
        case ConsultationRequestStatusDTO.COMPLETED:
          break
        default:
          throw new Error('Unsupported consultation status!')
      }

      return result.filter((a): a is Action => !!a)
    }

    if (isOutgoingRequest.value) {
      switch (consultation.value.status) {
        case ConsultationRequestStatusDTO.CREATED:
          result.push(deleteAction)
          break
        case ConsultationRequestStatusDTO.ACCEPTED:
        case ConsultationRequestStatusDTO.IN_PROGRESS:
        case ConsultationRequestStatusDTO.REPORT_PENDING:
          result.push(startCallAction, writeReportAction)
          break
        case ConsultationRequestStatusDTO.DELETED:
        case ConsultationRequestStatusDTO.COMPLETED:
          break
        default:
          throw new Error('Unsupported consultation status!')
      }
    } else {
      switch (consultation.value.status) {
        case ConsultationRequestStatusDTO.CREATED:
          result.push(declineAction, acceptAction)
          break
        case ConsultationRequestStatusDTO.ACCEPTED:
        case ConsultationRequestStatusDTO.IN_PROGRESS:
        case ConsultationRequestStatusDTO.REPORT_PENDING:
          result.push(startCallAction, writeReportAction)
          break
        case ConsultationRequestStatusDTO.DELETED:
        case ConsultationRequestStatusDTO.COMPLETED:
          break
        default:
          throw new Error('Unsupported consultation status!')
      }
    }

    return result.filter((a): a is Action => !!a)
  })

  return {
    consultationActionsPerStatus,
    consultationActions
  }
}

export default useConsultationActions
